import type { StoreUpdateCustomer } from "@medusajs/types";
import { type AnyFieldApi, useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type InputHTMLAttributes, useState } from "react";
import { z } from "zod";
import { PillBtn } from "#/components/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import { Field } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import { requestEmailChange, updateProfile } from "#/lib/auth-client";
import { notify } from "#/lib/notify";
import { sessionKeys, sessionQueries, useSession } from "#/lib/useSession";

const AUTH_FIELD_CLASS = "h-14 px-6";

const profileSchema = z.object({
  first_name: z.string().trim(),
  last_name: z.string().trim(),
  phone: z.string().trim(),
  company_name: z.string().trim(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const emailChangeSchema = z.object({
  new_email: z.string().email("Enter a valid email"),
});

type EmailChangeValues = z.infer<typeof emailChangeSchema>;

type TextFieldProps = {
  field: AnyFieldApi;
  label: string;
  type?: InputHTMLAttributes<HTMLInputElement>["type"];
  autoComplete?: string;
  required?: boolean;
  className?: string;
};

function TextField({
  field,
  label,
  type = "text",
  autoComplete,
  required,
  className = AUTH_FIELD_CLASS,
}: TextFieldProps) {
  const showError =
    field.state.meta.isTouched && field.state.meta.errors.length > 0;
  return (
    <Field label={label}>
      {(id) => (
        <>
          <Input
            id={id}
            name={field.name}
            type={type}
            value={String(field.state.value ?? "")}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            autoComplete={autoComplete}
            required={required}
            aria-invalid={showError}
            aria-describedby={showError ? `${field.name}-error` : undefined}
            className={className}
          />
          {showError && (
            <p
              id={`${field.name}-error`}
              role="alert"
              className="mt-1 font-display text-destructive text-xs"
            >
              {field.state.meta.errors
                .map((e) =>
                  typeof e === "string" ? e : (e?.message ?? "Invalid"),
                )
                .join(", ")}
            </p>
          )}
        </>
      )}
    </Field>
  );
}

export const Route = createFileRoute("/account/profile")({
  head: () => ({ meta: [{ title: "Profile" }] }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(sessionQueries.current()),
  component: ProfileTab,
});

function ProfileTab() {
  const { customer } = useSession();
  const queryClient = useQueryClient();
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: (values: ProfileFormValues) => {
      const body: StoreUpdateCustomer = {
        first_name: values.first_name.length > 0 ? values.first_name : null,
        last_name: values.last_name.length > 0 ? values.last_name : null,
        phone: values.phone.length > 0 ? values.phone : null,
        company_name:
          values.company_name.length > 0 ? values.company_name : null,
      };
      return updateProfile(body);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(sessionKeys.current, { customer: updated });
      notify.success("Profile updated");
    },
    onError: (err) => {
      notify.error(
        err instanceof Error
          ? err.message
          : "Could not update profile. Try again.",
      );
    },
  });

  const form = useForm({
    defaultValues: {
      first_name: customer?.first_name ?? "",
      last_name: customer?.last_name ?? "",
      phone: customer?.phone ?? "",
      company_name: customer?.company_name ?? "",
    } satisfies ProfileFormValues,
    validators: { onChange: profileSchema },
    onSubmit: ({ value }) => mutation.mutateAsync(value),
  });

  if (!customer) {
    return null;
  }

  return (
    <div>
      <h3 className="m-0 mb-2 font-bold font-display text-2xl text-lp-text">
        Profile
      </h3>
      <div className="mt-0 mb-8 flex flex-wrap items-center gap-3 font-display text-lp-text-lt">
        <span>
          Signed in as <strong>{customer.email}</strong>
        </span>
        <button
          type="button"
          onClick={() => setEmailModalOpen(true)}
          className="cursor-pointer rounded-full border-2 border-lp-green-mid bg-white px-4 py-1.5 font-display font-semibold text-lp-green-mid text-sm hover:bg-lp-green-soft"
        >
          Change email
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="grid max-w-2xl grid-cols-1 gap-5 md:grid-cols-2"
      >
        <form.Field name="first_name">
          {(field) => (
            <TextField
              field={field}
              label="First Name"
              autoComplete="given-name"
            />
          )}
        </form.Field>
        <form.Field name="last_name">
          {(field) => (
            <TextField
              field={field}
              label="Last Name"
              autoComplete="family-name"
            />
          )}
        </form.Field>
        <form.Field name="phone">
          {(field) => (
            <TextField
              field={field}
              label="Phone"
              type="tel"
              autoComplete="tel"
            />
          )}
        </form.Field>
        <form.Field name="company_name">
          {(field) => (
            <TextField
              field={field}
              label="Company"
              autoComplete="organization"
            />
          )}
        </form.Field>

        <div className="md:col-span-2 mt-2 flex justify-end">
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <PillBtn
                type="submit"
                variant="green"
                size="sm"
                disabled={!canSubmit || isSubmitting || mutation.isPending}
              >
                {isSubmitting || mutation.isPending
                  ? "Saving…"
                  : "Save Changes"}
              </PillBtn>
            )}
          </form.Subscribe>
        </div>
      </form>

      {emailModalOpen && (
        <ChangeEmailDialog
          currentEmail={customer.email}
          onClose={() => setEmailModalOpen(false)}
        />
      )}
    </div>
  );
}

interface ChangeEmailDialogProps {
  currentEmail: string;
  onClose: () => void;
}

function ChangeEmailDialog({ currentEmail, onClose }: ChangeEmailDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (values: EmailChangeValues) => {
      try {
        await requestEmailChange({ new_email: values.new_email });
        return { ok: true, email: values.new_email } as const;
      } catch (err) {
        if (err instanceof Error && /404|not.*found/i.test(err.message)) {
          return { ok: false, comingSoon: true } as const;
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.ok) {
        notify.success("Verification email sent", {
          description: `Check ${result.email} for the confirmation link.`,
        });
        onClose();
      } else {
        notify.info("Email change coming soon", {
          description: "This feature isn't enabled yet — try again later.",
        });
        onClose();
      }
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Could not request email change. Please try again.",
      );
    },
  });

  const form = useForm({
    defaultValues: { new_email: "" } satisfies EmailChangeValues,
    validators: { onChange: emailChangeSchema },
    onSubmit: ({ value }) => {
      setErrorMessage(null);
      return mutation.mutateAsync(value);
    },
  });

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      size="md"
    >
      <DialogHeader>
        <DialogTitle>Change email</DialogTitle>
        <DialogDescription>
          We&rsquo;ll send a verification link to your new address. Your current
          email <strong>{currentEmail}</strong> stays active until you confirm.
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <form.Field name="new_email">
            {(field) => (
              <TextField
                field={field}
                label="New email"
                type="email"
                autoComplete="email"
                required
              />
            )}
          </form.Field>

          {errorMessage && (
            <div className="font-display text-destructive text-sm">
              {errorMessage}
            </div>
          )}

          <DialogFooter className="px-0">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded-full bg-transparent px-5 py-2 font-display text-lp-text hover:bg-lp-grey"
            >
              Cancel
            </button>
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
              })}
            >
              {({ canSubmit, isSubmitting }) => (
                <PillBtn
                  type="submit"
                  variant="green"
                  size="sm"
                  disabled={!canSubmit || isSubmitting || mutation.isPending}
                >
                  {isSubmitting || mutation.isPending
                    ? "Sending…"
                    : "Send verification"}
                </PillBtn>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
